/**
 * Cache Refresh Progress Manager
 *
 * Singleton to manage progress state during cache refresh/version update.
 * Provides reactive state that can be consumed by Vue components.
 */

import { ref, readonly } from 'vue';

export interface RefreshStep {
  step: number;
  progress: number;
  message: string;
}

export const REFRESH_STEPS = {
  START: { step: 1, progress: 0, message: 'Starting update...' },
  CLEAR_LOCALSTORAGE: { step: 2, progress: 20, message: 'Clearing local storage...' },
  CLEAR_CACHES: { step: 3, progress: 40, message: 'Clearing browser caches...' },
  UNREGISTER_SW: { step: 4, progress: 60, message: 'Unregistering service workers...' },
  PREPARE: { step: 5, progress: 80, message: 'Preparing new version...' },
  RELOAD: { step: 6, progress: 100, message: 'Reloading application...' },
} as const;

// Reactive state
const isActive = ref(false);
const currentStep = ref(0);
const progress = ref(0);
const message = ref('');
const fromVersion = ref('');
const toVersion = ref('');

// Animation state
let animationFrameId: number | null = null;
let targetProgress = 0;
const ANIMATION_SPEED = 0.15; // Progress units per frame (higher = faster)
const MICRO_PROGRESS_SPEED = 0.02; // Small continuous progress between steps

/**
 * Start the cache refresh progress overlay
 */
function start(from: string, to: string): void {
  // Cancel any existing animation
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }

  fromVersion.value = from;
  toVersion.value = to;
  currentStep.value = REFRESH_STEPS.START.step;
  progress.value = REFRESH_STEPS.START.progress;
  targetProgress = REFRESH_STEPS.START.progress;
  message.value = REFRESH_STEPS.START.message;
  isActive.value = true;
}

/**
 * Smoothly animate progress to target value
 * Uses a combination of:
 * 1. Ease-out animation toward the target
 * 2. Continuous micro-progress to prevent "stuck" feeling
 */
function animateProgress(): void {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
  }

  const animate = () => {
    const current = progress.value;
    const diff = targetProgress - current;

    // If we're at or past target, just hold at target (don't overshoot)
    if (diff <= 0) {
      progress.value = targetProgress;
      // Keep animation running for continuous micro-progress when next target comes
      animationFrameId = requestAnimationFrame(animate);
      return;
    }

    // Calculate smooth step using ease-out + micro-progress
    // The micro-progress ensures there's always visible movement
    const easeStep = diff * ANIMATION_SPEED;
    const microStep = MICRO_PROGRESS_SPEED;
    const step = Math.max(easeStep, microStep);

    // Apply step and round to 1 decimal place for smooth display
    const newProgress = Math.min(targetProgress, current + step);
    progress.value = Math.round(newProgress * 10) / 10;

    animationFrameId = requestAnimationFrame(animate);
  };

  animationFrameId = requestAnimationFrame(animate);
}

/**
 * Update progress to a specific step
 */
function updateProgress(step: RefreshStep): void {
  currentStep.value = step.step;
  targetProgress = step.progress;
  message.value = step.message;

  // Start smooth animation to target
  animateProgress();
}

/**
 * Complete the refresh process
 * Immediately snaps to 100% for clean finish before reload
 */
function complete(): void {
  // Cancel any running animation
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }

  // Snap directly to 100% for clean finish
  currentStep.value = REFRESH_STEPS.RELOAD.step;
  targetProgress = 100;
  progress.value = 100;
  message.value = REFRESH_STEPS.RELOAD.message;
}

/**
 * Reset state (used for cleanup/testing)
 */
function reset(): void {
  // Cancel any running animation
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }

  isActive.value = false;
  currentStep.value = 0;
  progress.value = 0;
  targetProgress = 0;
  message.value = '';
  fromVersion.value = '';
  toVersion.value = '';
}

/**
 * Get readonly reactive state for use in components
 */
export function useCacheRefreshProgress() {
  return {
    isActive: readonly(isActive),
    currentStep: readonly(currentStep),
    progress: readonly(progress),
    message: readonly(message),
    fromVersion: readonly(fromVersion),
    toVersion: readonly(toVersion),
  };
}

/**
 * Get mutable controls for the progress manager
 * Used by versionRefresh.ts to control the overlay
 */
export function getCacheRefreshProgressControls() {
  return {
    start,
    updateProgress,
    complete,
    reset,
    STEPS: REFRESH_STEPS,
  };
}
